///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import { AbstractInteractiveWindow } from '../../abstract-interactive-window';
import { ActivatedRoute } from '@angular/router';
import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { GtthHistoryService } from '../../../logical-topology/engine/history-service';
import { ProjectInteractiveWindowsPopupService } from '../../project-interactive-windows-popup.service';
import { PARAM_BBOX } from '../../../../shared/constants/url-params.constants';
import { NodeAnIpInfoModel } from './node-an-ip-data.model';
import { Subscription } from 'rxjs';
import { GtthReportsTabs, REPORT_TABS } from '../reports-tabs.enum';
import { GtthAnIpInfoService } from './an-ip-info.service';
import { NgForm, NgModel } from '@angular/forms';
import { IP_OPTIONAL_VALUE, IP_ZERO_VALUE } from '../../../../shared/directives/validation-custom/ip-address.validator';
import { debounceTime } from 'rxjs/operators';

@Component({
    selector: 'winde-an-ip-info',
    templateUrl: 'an-ip-info.component.html',
    styleUrls: ['an-ip-info.component.scss', '../../project-windows.scss'],
    standalone: false
})
export class GtthAnIpInfoWindowComponent extends AbstractInteractiveWindow implements OnInit, AfterViewInit, OnDestroy {
    @ViewChild('reportContainer') reportContainer: ElementRef;
    @ViewChild(NgForm) ngForm: NgForm;
    @ViewChildren(NgModel) ngModelList: QueryList<NgModel>;

    public IP_ZERO_VALUE = IP_ZERO_VALUE;
    public IP_OPTIONAL_VALUE = IP_OPTIONAL_VALUE;

    public nodesData: NodeAnIpInfoModel[] = [];
    public readonly tabsToShow = REPORT_TABS;
    public currentTab = GtthReportsTabs.ALL;

    private currentLayerIndex: number;

    private layersChangedSubscriber: Subscription;
    private valueChangesSubscription: Subscription;

    constructor(
        public historyService: GtthHistoryService,
        public anIpInfoService: GtthAnIpInfoService,
        private readonly elementRef: ElementRef,
        private readonly dialogRef: MatDialogRef<GtthAnIpInfoWindowComponent>
    ) {
        super(elementRef);
    }

    ngOnInit() {
        this.currentLayerIndex = this.historyService.projectInfoViewService.activeLayer;
        this.getReportData();

        this.layersChangedSubscriber = this.historyService.projectInfoViewService.layerChangedSubscription().subscribe((value) => {
            this.currentLayerIndex = value;
            this.getReportData();
        });
    }

    ngAfterViewInit(): void {
        this.valueChangesSubscription = this.ngForm.form.valueChanges.pipe(debounceTime(500)).subscribe(() => {
            setTimeout(() => {
                this.ngModelList.forEach((ngModel: NgModel) => {
                    ngModel.control.updateValueAndValidity({ onlySelf: true, emitEvent: false });
                });
            });
        });
    }

    ngOnDestroy() {
        if (this.valueChangesSubscription) {
            this.valueChangesSubscription.unsubscribe();
            this.valueChangesSubscription = undefined;
        }
        if (this.layersChangedSubscriber) {
            this.layersChangedSubscriber.unsubscribe();
            this.layersChangedSubscriber = undefined;
        }
    }

    getReportData(): void {
        this.nodesData = [];
        const allNodes = this.anIpInfoService.getAllReportNodesList(this.currentLayerIndex, this.currentTab);
        allNodes.forEach((nodeName: string) => {
            const nodeData = this.anIpInfoService.getNodeAnIpData(this.currentLayerIndex, this.currentTab, nodeName);
            if (nodeData.length) {
                this.nodesData.push({ nodeName, devicesData: nodeData });
            }
        });
    }

    changeTab(tabIndex: string) {
        this.currentTab = GtthReportsTabs[tabIndex];
        this.reportContainer.nativeElement.scrollTop = 0;

        this.getReportData();
    }

    clear() {
        this.dialogRef.close('cancel');
    }

    designRequired(): boolean {
        return true;
    }

    isRestorable(): boolean {
        return true;
    }
}

@Component({
    selector: 'winde-an-ip-info-popup',
    template: '',
    standalone: false
})
export class GtthAnIpInfoPopupComponent implements OnInit {
    constructor(
        private readonly interactiveWindowsPopupService: ProjectInteractiveWindowsPopupService,
        private readonly activatedRoute: ActivatedRoute
    ) {}

    ngOnInit() {
        let bboxParams;
        if (this.activatedRoute.snapshot.queryParamMap.has(PARAM_BBOX)) {
            bboxParams = this.activatedRoute.snapshot.queryParamMap
                .get(PARAM_BBOX)
                .split(',')
                .map((value) => parseFloat(value));
        }
        const routerPath = this.activatedRoute.routeConfig.path;
        this.interactiveWindowsPopupService.openInteractiveWindow(
            GtthAnIpInfoWindowComponent,
            ProjectInteractiveWindowsPopupService.AN_IP_REPORT,
            routerPath,
            bboxParams
        );
    }
}
